import { Dispatch, SetStateAction, useState } from 'react';

import { useLocation } from 'react-router';

import { useTranslation } from 'react-i18next';

import { useFlags } from '@/hooks/useFlags';
import type { AddSortFields } from '@/hooks/useSortOptions';

import ManagedPlansCard from '@/components/Cards/PlanCard/PlanCards/ManagedPlans/ManagedPlansCard';
import ManagePlansCompliancePlanCard from '@/components/Cards/PlanCard/PlanCards/ManagePlansComplianceCard';
import ClearFiltersHeader from '@/components/ClearFiltersHeader/ClearFiltersHeader';
import { Label } from '@/components/Filter/components/FilterCheckboxSection';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';

import CreatePlanFab from './CreatePlanFab';
import AddTeamMembers from './Modals/AddTeamMembers';
import DeletePlan from './Modals/DeletePlan';
import DuplicatePlan from './Modals/DuplicatePlan';
import EditTranslations from './Modals/EditTranslations';
import RemovePlan from './Modals/RemovePlan';
import RenamePlan from './Modals/RenamePlan';
import TrainTogether from './Modals/TrainTogether';

import { Plan } from '@/_generated/api-clients/xp';
import { useDataView } from '@/atoms/data-view';
import { useAddReadOnlyPlan } from '@/routes/TrainingPlans/hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';

interface PlanCardsProps {
  categoryLabels: Label;
  currentFilters: string[];
  filteredAndSortedPlans: Plan[];
  isFetchingPlans: boolean;
  onShowPopUp: (id: string, name: string) => void;
  refetchPlans: () => void;
  searchFilter: string;
  setShowCustomPlan: Dispatch<SetStateAction<boolean>>;
  setShowTemplateModal: Dispatch<SetStateAction<boolean>>;
}

const PlanCards: React.FC<PlanCardsProps> = ({
  categoryLabels,
  currentFilters,
  filteredAndSortedPlans,
  isFetchingPlans,
  onShowPopUp,
  refetchPlans,
  searchFilter,
  setShowCustomPlan,
  setShowTemplateModal,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { xpCompliancePlans } = useFlags();
  const isCompliance = search.includes('compliancePlan');
  const [showAddPlanPopUp, setShowAddPlanPopUp] = useState(false);
  const [showAddTeamMembersPopup, setShowAddTeamMembersPopup] = useState(false);
  const [showRenamePlan, setShowRenamePlan] = useState(false);
  const [showDueDatePopup, setShowDueDatePopup] = useState(false);
  const [editTranslations, setEditTranslations] = useState(false);
  const [showDuplicatePlan, setShowDuplicatePlan] = useState(false);
  const [showDeletePlan, setShowDeletePlan] = useState(false);
  const [showRemovePlan, setShowRemovePlan] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const [
    {
      display: { items: dataViewItems, total },
    },
    { showMore, resetCheckboxByValue, resetAllCheckboxFilters },
  ] = useDataView<AddSortFields<Plan>>('manage-plans');

  const { unsubscribeReadonlyPlan, isPending: isRemovePending } =
    useAddReadOnlyPlan();

  const handleRemove = async () => {
    if (!selectedPlan) return;
    await unsubscribeReadonlyPlan({
      id: selectedPlan.id,
      name: selectedPlan.name,
    });
    setShowRemovePlan(false);
  };

  return (
    <>
      <div className="relative max-w-full grow gap-6">
        <div className="flex max-w-full grow flex-col">
          <ClearFiltersHeader
            aliases={categoryLabels}
            clearAllFilters={() => {
              resetAllCheckboxFilters();
            }}
            clearFilter={value => {
              resetCheckboxByValue(value);
            }}
            filters={currentFilters}
          />

          <div className="relative flex max-w-full grow flex-col">
            {!filteredAndSortedPlans.length &&
              !isFetchingPlans &&
              !currentFilters.length &&
              !searchFilter && (
                <NoMessage
                  message={t('TrainingPlans.createPlanOnBuildPlanPage')}
                />
              )}
            {(!!currentFilters.length || !!searchFilter) && !total && (
              <NoMessage message={t('TrainingPlans.noPlansResults')} />
            )}
            {!!filteredAndSortedPlans.length && !isFetchingPlans && (
              <div className="flex flex-col gap-4">
                {filteredAndSortedPlans.map(plan =>
                  plan.type !== 'COMPLIANCE' || xpCompliancePlans ? (
                    <ManagedPlansCard
                      key={plan.id}
                      assignedUsers={plan.assignedUsers}
                      assignPlan={() => {
                        setSelectedPlan(plan);
                        setShowAddTeamMembersPopup(true);
                      }}
                      completedUsers={plan.completedUsers}
                      deletePlan={() => {
                        setSelectedPlan(plan);
                        setShowDeletePlan(true);
                      }}
                      duplicatePlan={() => {
                        setSelectedPlan(plan);
                        setShowDuplicatePlan(true);
                      }}
                      editTranslations={() => {
                        setSelectedPlan(plan);
                        setEditTranslations(true);
                      }}
                      plan={plan}
                      removePlan={() => {
                        setSelectedPlan(plan);
                        setShowRemovePlan(true);
                      }}
                      renamePlan={() => {
                        setSelectedPlan(plan);
                        setShowRenamePlan(true);
                      }}
                      sendPlanToParent={() => setSelectedPlan(plan)}
                    />
                  ) : (
                    <ManagePlansCompliancePlanCard
                      course={plan}
                      key={plan.id}
                      onShowEnableCompliancePlan={onShowPopUp}
                    />
                  ),
                )}
                <LoadMorePaginator
                  onClick={() => showMore()}
                  showing={dataViewItems.length}
                  showingText={t('TrainingPlans.showingXOfYPlans', {
                    showing: dataViewItems.length,
                    total,
                  })}
                  total={total}
                />
              </div>
            )}
          </div>
        </div>
        <CreatePlanFab
          setShowAddPlanPopUp={setShowAddPlanPopUp}
          setShowCustomPlan={setShowCustomPlan}
          setShowTemplateModal={setShowTemplateModal}
          showAddPlanPopUp={showAddPlanPopUp}
        />
      </div>
      <AddTeamMembers
        plan={selectedPlan ?? undefined}
        selectedId={selectedId}
        showAddTeamMembersPopup={showAddTeamMembersPopup}
        showDueDatePopup={showDueDatePopup}
        setSelectedId={setSelectedId}
        setShowDueDatePopup={setShowDueDatePopup}
        setShowAddTeamMembersPopup={setShowAddTeamMembersPopup}
      />
      {selectedPlan && (
        <TrainTogether
          isCompliance={isCompliance}
          plan={selectedPlan}
          setSelectedId={setSelectedId}
          setShowAddTeamMembersPopup={setShowAddTeamMembersPopup}
          setShowDueDatePopup={setShowDueDatePopup}
        />
      )}
      {selectedPlan && (
        <>
          <RenamePlan
            plan={selectedPlan}
            setShowRenamePlan={setShowRenamePlan}
            showRenamePlan={showRenamePlan}
          />
          <EditTranslations
            editTranslations={editTranslations}
            plan={selectedPlan}
            refetchPlans={refetchPlans}
            setEditTranslations={setEditTranslations}
          />
          <DuplicatePlan
            plan={selectedPlan}
            refetchPlans={refetchPlans}
            setShowDuplicatePlan={setShowDuplicatePlan}
            showDuplicatePlan={showDuplicatePlan}
          />
          <DeletePlan
            plan={selectedPlan}
            refetchPlans={refetchPlans}
            setShowDeletePlan={setShowDeletePlan}
            showDeletePlan={showDeletePlan}
          />
          <RemovePlan
            isLoading={isRemovePending}
            onRemove={handleRemove}
            plan={selectedPlan}
            setShowRemovePlan={setShowRemovePlan}
            showRemovePlan={showRemovePlan}
          />
        </>
      )}
    </>
  );
};

export default PlanCards;
